var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef, useState, memo, } from 'react';
import { t } from 'i18next';
import '../styles/widgets/numberCharts.scss';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import globalStore from '../store';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { selectTool } from '../helper/helper';
import { HIGHLIGHT_COLOR, SPEED_TIME, INDEX_COLOR_MAP, getColorInfo, mapRowColByDirection, mapRevealIndexToRowCol, getClientPoint, compareCellValues, isAnyCellHidden, getStampElements, } from '../helper/numberChartHelper';
import { announce } from '../helper/announcer';
import { useDuplicateAnnounce, useCreationAnnounce } from '../helper/hooks/useDuplicateAnnounce';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var CellValue = memo(function (_a) {
    var value = _a.value, visible = _a.visible, fontSize = _a.fontSize, _b = _a.color, color = _b === void 0 ? '#293273' : _b;
    if (!value || !visible) {
        return _jsx("div", { style: { opacity: 0 } });
    }
    // Strip MathJax delimiters: \(...\)
    var cleanValue = value.replace(/^\\\(/, '').replace(/\\\)$/, '');
    // Guard against invalid values (NaN/Infinity from bad denominator input)
    if (cleanValue.includes('NaN') || cleanValue.includes('Infinity')) {
        return _jsx("div", { style: { opacity: 0 } });
    }
    // Parse the value to determine type
    // Integer: "10" or "-10"
    // Fraction: "\frac{1}{2}" or "-\frac{1}{2}"
    // Mixed number: "2\frac{3}{4}" or "-2\frac{3}{4}"
    // Decimal: "10.00" or "-10.00"
    // Check for negative sign
    var isNegative = cleanValue.startsWith('-');
    var workValue = isNegative ? cleanValue.slice(1) : cleanValue;
    // Check for mixed number: has both integer and \frac
    var mixedMatch = workValue.match(/^(\d+)\\frac\{(\d+)\}\{(\d+)\}$/);
    if (mixedMatch) {
        var integer = mixedMatch[1], numerator = mixedMatch[2], denominator = mixedMatch[3];
        var sign = isNegative ? 'negative ' : '';
        var ariaLabel = t('text.numberChartAriaMixed', { sign: sign, integer: integer, numerator: numerator, denominator: denominator });
        return (_jsxs("div", { role: "math", "aria-label": ariaLabel, style: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                fontSize: "".concat(fontSize, "px"),
                color: color,
                fontWeight: 500,
                lineHeight: 1,
            }, children: [isNegative && _jsx("span", { "aria-hidden": "true", children: "\u2212" }), _jsx("span", { "aria-hidden": "true", children: integer }), _jsxs("div", { style: {
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        fontSize: "".concat(fontSize * 0.7, "px"),
                        lineHeight: 0.9,
                    }, "aria-hidden": "true", children: [_jsx("span", { style: { textAlign: 'center' }, children: numerator }), _jsx("span", { style: {
                                borderTop: '1px solid currentColor',
                                paddingTop: '1px',
                                textAlign: 'center',
                                width: '100%',
                            }, children: denominator })] })] }));
    }
    // Check for fraction: \frac{numerator}{denominator}
    var fracMatch = workValue.match(/^\\frac\{(\d+)\}\{(\d+)\}$/);
    if (fracMatch) {
        var numerator = fracMatch[1], denominator = fracMatch[2];
        var sign = isNegative ? 'negative ' : '';
        var ariaLabel = t('text.numberChartAriaFraction', { sign: sign, numerator: numerator, denominator: denominator });
        return (_jsxs("div", { role: "math", "aria-label": ariaLabel, style: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
                fontSize: "".concat(fontSize * 0.9, "px"),
                color: color,
                fontWeight: 500,
                lineHeight: 0.9,
                position: 'relative',
                minWidth: 'fit-content',
            }, children: [isNegative && (_jsx("span", { style: { position: 'absolute', left: '-0.5em' }, "aria-hidden": "true", children: "\u2212" })), _jsx("span", { style: { textAlign: 'center' }, "aria-hidden": "true", children: numerator }), _jsx("span", { style: {
                        borderTop: '1px solid currentColor',
                        paddingTop: '1px',
                        textAlign: 'center',
                        width: '100%',
                    }, "aria-hidden": "true", children: denominator })] }));
    }
    // Integer or decimal - simple text
    return (_jsx("div", { style: {
            fontSize: "".concat(fontSize, "px"),
            color: color,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }, children: isNegative ? "\u2212".concat(workValue) : workValue }));
}, function (prevProps, nextProps) {
    // Custom comparison for memoization - only re-render if these props change
    return (prevProps.value === nextProps.value &&
        prevProps.visible === nextProps.visible &&
        prevProps.fontSize === nextProps.fontSize &&
        prevProps.color === nextProps.color);
});
CellValue.displayName = 'CellValue';
/**
 * Memoized Cell component to prevent unnecessary re-renders
 * Only re-renders when cell data, position, or styling changes
 */
// Stamp shape translation keys matching btnData stamp element order (index 0-10)
var STAMP_SHAPE_KEYS = [
    'text.solidSquare',
    'text.transparentSquare',
    'text.stampShape2',
    'text.stampShape3',
    'text.stampShape4',
    'text.stampShape5',
    'text.stampShape6',
    'text.stampShape7',
    'text.stampShape8',
    'text.stampShape9',
    'text.stampShape10',
];
// Color translation key mapping from INDEX_COLOR_MAP values (e.g., "RED" → "text.colorRed")
var colorTranslationKey = function (colorIdx) {
    var _a;
    var colorName = (_a = INDEX_COLOR_MAP[colorIdx]) !== null && _a !== void 0 ? _a : 'BLACK';
    return "text.color".concat(colorName.charAt(0)).concat(colorName.slice(1).toLowerCase());
};
// Convert cell value (possibly LaTeX) to a screen-reader-friendly string
var readableValue = function (value) {
    if (!value)
        return '';
    var clean = value.replace(/^\\\(/, '').replace(/\\\)$/, '');
    if (clean.includes('NaN') || clean.includes('Infinity'))
        return '';
    var isNeg = clean.startsWith('-');
    var work = isNeg ? clean.slice(1) : clean;
    var sign = isNeg ? 'negative ' : '';
    var mixedMatch = work.match(/^(\d+)\\frac\{(\d+)\}\{(\d+)\}$/);
    if (mixedMatch) {
        return t('text.numberChartAriaMixed', {
            sign: sign,
            integer: mixedMatch[1],
            numerator: mixedMatch[2],
            denominator: mixedMatch[3],
        });
    }
    var fracMatch = work.match(/^\\frac\{(\d+)\}\{(\d+)\}$/);
    if (fracMatch) {
        return t('text.numberChartAriaFraction', {
            sign: sign,
            numerator: fracMatch[1],
            denominator: fracMatch[2],
        });
    }
    return "".concat(sign).concat(work);
};
var buildCellAriaLabel = function (cellData, row, col) {
    var _a;
    var parts = [];
    // Value or hidden
    if (cellData.visible) {
        parts.push(readableValue(cellData.value));
    }
    else {
        parts.push(t('text.hidden'));
    }
    // Stamps
    if (((_a = cellData.iconIndex) === null || _a === void 0 ? void 0 : _a.length) > 0) {
        var stampDescs = cellData.iconIndex.map(function (stampIdx, i) {
            var _a, _b, _c;
            var color = t(colorTranslationKey((_b = (_a = cellData.colorIndex) === null || _a === void 0 ? void 0 : _a[i]) !== null && _b !== void 0 ? _b : 0));
            var shape = t((_c = STAMP_SHAPE_KEYS[stampIdx]) !== null && _c !== void 0 ? _c : 'text.stamp');
            return "".concat(color, " ").concat(shape);
        });
        parts.push(stampDescs.join(', ') + ' ' + t('text.stamp'));
    }
    // Highlight (pattern game / choral counting) — include position
    if (cellData.highlightColor) {
        parts.push(t('text.highlighted'));
        if (row !== undefined && col !== undefined) {
            parts.push(t('text.cellPosition', { row: row + 1, col: col + 1 }));
        }
    }
    return parts.join(', ');
};
var NumberChartCell = memo(function (_a) {
    var _b;
    var cellData = _a.cellData, rowIdx = _a.rowIdx, colIdx = _a.colIdx, r = _a.r, c = _a.c, uniformFontSize = _a.uniformFontSize, showGridLines = _a.showGridLines, stamps = _a.stamps, cellBorderWidth = _a.cellBorderWidth, cellTabIndex = _a.cellTabIndex, cellRef = _a.cellRef, onCellKeyDown = _a.onCellKeyDown, isStamped = _a.isStamped;
    if (!cellData)
        return null;
    var _c = cellData.value, value = _c === void 0 ? '' : _c, visible = cellData.visible, highlightColor = cellData.highlightColor;
    return (_jsxs("div", { className: "cell", role: "gridcell", tabIndex: cellTabIndex, "aria-label": buildCellAriaLabel(cellData, r, c), "aria-selected": !!isStamped, ref: cellRef, onKeyDown: onCellKeyDown ? function (e) { return onCellKeyDown(e, r, c); } : undefined, "data-row": rowIdx, "data-col": colIdx, style: (_b = {
                border: "".concat(cellBorderWidth, "px solid ").concat(showGridLines ? '#4586C0' : 'transparent')
            },
            _b['--cell-font-size'] = "".concat(uniformFontSize, "px"),
            _b.backgroundColor = highlightColor || 'transparent',
            _b), children: [stamps.map(function (stamp, idx) { return (
            // <div
            //   key={idx}
            //   className={`imgHolder ${stamp.isOpaque ? 'opaque' : ''}`}
            //   style={{
            //     backgroundImage: `url(${stamp.url})`,
            //   }}
            // ></div>
            _jsx("img", { src: stamp.url, alt: "", draggable: false, className: "imgHolder ".concat(stamp.isOpaque ? 'opaque' : '') }, idx)); }), _jsx("div", { className: "text", children: _jsx(CellValue, { value: value, visible: visible, fontSize: uniformFontSize, color: "#293273" }) })] }, "".concat(r, "-").concat(c)));
}, function (prevProps, nextProps) {
    var _a, _b;
    // Only re-render if relevant props change
    if (!prevProps.cellData || !nextProps.cellData)
        return false;
    // Compare iconIndex arrays by value, not just length
    var prevIconIndex = (_a = prevProps.cellData.iconIndex) !== null && _a !== void 0 ? _a : [];
    var nextIconIndex = (_b = nextProps.cellData.iconIndex) !== null && _b !== void 0 ? _b : [];
    var iconIndexEqual = prevIconIndex.length === nextIconIndex.length &&
        prevIconIndex.every(function (val, idx) { return val === nextIconIndex[idx]; });
    // Compare stamps arrays by value (url and isOpaque)
    var stampsEqual = prevProps.stamps.length === nextProps.stamps.length &&
        prevProps.stamps.every(function (stamp, idx) {
            var _a, _b;
            return stamp.url === ((_a = nextProps.stamps[idx]) === null || _a === void 0 ? void 0 : _a.url) &&
                stamp.isOpaque === ((_b = nextProps.stamps[idx]) === null || _b === void 0 ? void 0 : _b.isOpaque);
        });
    return (prevProps.cellData.value === nextProps.cellData.value &&
        prevProps.cellData.visible === nextProps.cellData.visible &&
        prevProps.cellData.highlightColor === nextProps.cellData.highlightColor &&
        iconIndexEqual &&
        prevProps.uniformFontSize === nextProps.uniformFontSize &&
        prevProps.showGridLines === nextProps.showGridLines &&
        prevProps.cellBorderWidth === nextProps.cellBorderWidth &&
        prevProps.cellTabIndex === nextProps.cellTabIndex &&
        prevProps.isStamped === nextProps.isStamped &&
        stampsEqual);
});
NumberChartCell.displayName = 'NumberChartCell';
var NumberCharts = function (props) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var data = props.data, id = props.id, _j = props.calledFrom, calledFrom = _j === void 0 ? 'popup' : _j, _k = props.displayScale, displayScale = _k === void 0 ? 1 : _k;
    // When the popup scales the chart down, compensate so 1px borders remain visible
    var cellBorderWidth = calledFrom === 'popup' && displayScale < 1 ? Math.round((1 / displayScale) * 10) / 10 : 1;
    // `cells` can briefly be undefined when a chart is reconstructed from a
    // share-code payload that didn't preserve the grid (or during a data
    // mid-update). Default to an empty grid so the render-time useMemos
    // (uniformFontSize, dynamicCellSize) don't crash on `cells.forEach`.
    var rows = data.rows, columns = data.columns, direction = data.direction, _l = data.cells, cells = _l === void 0 ? [] : _l;
    var color = useContext(DataContext).color;
    var _m = useContext(DataContext), setManipulative = _m.setManipulative, setManiplulatorComponentVisible = _m.setManiplulatorComponentVisible, setActiveManipulator = _m.setActiveManipulator, setSelectedManipulativeId = _m.setSelectedManipulativeId, selectedTool = _m.selectedTool, setSelectedTool = _m.setSelectedTool, panningEnable = _m.panningEnable, manipulative = _m.manipulative, setContextMenu = _m.setContextMenu;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var duplicateLabel = useDuplicateAnnounce(id, t('text.manipulativeNumberChart'));
    var creationLabel = useCreationAnnounce(id, t('text.manipulativeNumberChart'));
    var _o = useState(false), isFocused = _o[0], setIsFocused = _o[1];
    var _p = useState(false), isDraggingSelf = _p[0], setIsDraggingSelf = _p[1];
    // Layer 2 detection: VoiceOver sets :focus-visible on focus; plain touch doesn't.
    var lastFocusWasScreenReader = useRef(false);
    // Layer 3 detection: a VoiceOver synthetic click has no matching touchstart/end
    // in the immediate past (user isn't physically touching the element).
    var lastTouchEventTimeRef = useRef(0);
    var _q = useState((_a = data.stampIndex) !== null && _a !== void 0 ? _a : 0), stampIndex = _q[0], setStampIndex = _q[1];
    var _r = useState((_b = data.actionType) !== null && _b !== void 0 ? _b : null), actionType = _r[0], setActionType = _r[1];
    var _s = useState((_c = data.isGameMode) !== null && _c !== void 0 ? _c : false), isGameMode = _s[0], setIsGameMode = _s[1];
    var _t = useState((_d = data.currentGame) !== null && _d !== void 0 ? _d : null), currentGame = _t[0], setCurrentGame = _t[1];
    var _u = useState(null), patternAnnouncement = _u[0], setPatternAnnouncement = _u[1];
    var _v = useState((_e = data.choralDirectionIndex) !== null && _e !== void 0 ? _e : 0), revealDirectionIndex = _v[0], setRevealDirectionIndex = _v[1];
    var revealDirectionIndexRef = useRef(revealDirectionIndex);
    var animationRef = useRef(null);
    var intervalRef = useRef(null);
    var indexRef = useRef(0);
    var pausedRef = useRef(false);
    // True from the moment a choral reveal genuinely starts (Play or Next)
    // until it's consumed by a close-driven reset or a manual show/hide
    // action. Lets the implicit 'choralReset' guard tell "a reveal happened
    // and hasn't been resolved yet" (should force all cells visible on close)
    // apart from "no choral session touched this chart at all" (closing the
    // popup must not override cell visibility the user set some other way).
    var choralSessionActiveRef = useRef(false);
    var stampIndexRef = useRef(stampIndex);
    var colorRef = useRef(color);
    var dragStartRef = useRef(null);
    var initialCenterRef = useRef(null);
    var _w = data === null || data === void 0 ? void 0 : data.lock, _x = _w.positioning, positionLock = _x === void 0 ? false : _x, _y = _w.editing, editingLock = _y === void 0 ? false : _y;
    var wrapperRef = useRef(null);
    var positionDragRef = useRef(null);
    var isFocusedRef = useRef(false);
    var dataRef = useRef(data);
    var lastTouchTimeRef = useRef(0);
    var _z = useState(false), gridNavActive = _z[0], setGridNavActive = _z[1];
    var _0 = useState({ row: 0, col: 0 }), activeCell = _0[0], setActiveCell = _0[1];
    var cellRefs = useRef(new Map());
    /** ---------------- KEEP DATA REF IN SYNC ---------------- */
    useEffect(function () {
        dataRef.current = data;
    }, [data]);
    /** ---------------- IS FOCUSED ---------------- */
    useEffect(function () {
        var focused = selectedTool.includes(id);
        setIsFocused(focused);
        isFocusedRef.current = focused;
        // Reset actionType when component loses focus, except 'clear' (show/hide numbers),
        // which is a persistent mode that should survive deselect/reselect
        if (!focused && actionType !== 'clear') {
            setActionType('');
        }
        // Auto-focus when selected (guard: don't steal from context menu)
        if (focused && calledFrom === 'widget') {
            requestAnimationFrame(function () {
                var _a, _b;
                if (!((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu'))) {
                    var btn = (_b = wrapperRef.current) === null || _b === void 0 ? void 0 : _b.querySelector('.gridParent');
                    if (btn && document.activeElement !== btn)
                        btn.focus();
                }
            });
        }
    }, [selectedTool, id, calledFrom]);
    /** ---------------- KEEP STAMP INDEX REF IN SYNC ---------------- */
    useEffect(function () {
        stampIndexRef.current = stampIndex;
    }, [stampIndex]);
    /** ---------------- KEEP ACTION TYPE REF IN SYNC ---------------- */
    var actionTypeRef = useRef(actionType);
    useEffect(function () {
        actionTypeRef.current = actionType;
    }, [actionType]);
    /** ---------------- KEEP REVEAL DIRECTION INDEX REF IN SYNC ---------------- */
    useEffect(function () {
        revealDirectionIndexRef.current = revealDirectionIndex;
    }, [revealDirectionIndex]);
    /** ---------------- KEEP COLOR REF IN SYNC ---------------- */
    useEffect(function () {
        colorRef.current = color;
    }, [color]);
    /** ---------------- CLEANUP ON UNMOUNT ---------------- */
    useEffect(function () {
        return function () {
            // Clear any running intervals
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            // Remove any window event listeners that might still be attached
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchmove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('touchend', onMouseUp);
        };
    }, []);
    /** ---------------- SYNC STATE TO MANIPULATIVE DATA ---------------- */
    useEffect(function () {
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.type === 'numberCharts' && item.data.id === id
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { isGameMode: isGameMode, currentGame: currentGame, stampIndex: stampIndex, actionType: actionType !== null && actionType !== void 0 ? actionType : undefined, choralDirectionIndex: revealDirectionIndex }) }) : item;
            });
        });
    }, [isGameMode, currentGame, stampIndex, actionType, revealDirectionIndex, id, setManipulative]);
    /** ---------------- DISPATCH ACTION TYPE STATE FOR BUTTON ACTIVE STATE ---------------- */
    useEffect(function () {
        // Emit event to notify RightSideToolPanel about actionType changes
        customEventHandler.dispatchEvent('numberChartActionType', {
            id: id,
            actionType: actionType,
        });
    }, [actionType, id]);
    /** ---------------- BOTTOM PANEL EVENT ---------------- */
    useEffect(function () {
        var onToolClick = function (data) {
            if (!isFocusedRef.current)
                return;
            // console.log(data, '%%%');
            switch (data.shapeType) {
                case 'MissingNumber':
                    setActionType('');
                    setIsGameMode(true);
                    setCurrentGame('MissingNumber');
                    missingNumberShape();
                    break;
                case 'OneMoreOneLess':
                    setActionType('');
                    setIsGameMode(true);
                    setCurrentGame('OneMoreOneLess');
                    drawOneLessOneMore();
                    break;
                case 'Line': {
                    setActionType('');
                    setIsGameMode(true);
                    setCurrentGame('Line');
                    var lineCount = drawLineShape();
                    if (lineCount) {
                        setPatternAnnouncement(t('text.patternApplied', { shape: 'Line', count: lineCount }));
                        setTimeout(function () { var _a, _b; return (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.gridParent')) === null || _b === void 0 ? void 0 : _b.focus(); }, 100);
                    }
                    break;
                }
                case 'Square': {
                    setActionType('');
                    setIsGameMode(true);
                    setCurrentGame('Square');
                    var squareCount = drawSquareShape();
                    if (squareCount) {
                        setPatternAnnouncement(t('text.patternApplied', { shape: 'Square', count: squareCount }));
                        setTimeout(function () { var _a, _b; return (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.gridParent')) === null || _b === void 0 ? void 0 : _b.focus(); }, 100);
                    }
                    break;
                }
                case 'Circle': {
                    setActionType('');
                    setIsGameMode(true);
                    setCurrentGame('Circle');
                    var circleCount = drawCircleShape();
                    if (circleCount) {
                        setPatternAnnouncement(t('text.patternApplied', { shape: 'Circle', count: circleCount }));
                        setTimeout(function () { var _a, _b; return (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.gridParent')) === null || _b === void 0 ? void 0 : _b.focus(); }, 100);
                    }
                    break;
                }
                case 'HShape': {
                    setActionType('');
                    setIsGameMode(true);
                    setCurrentGame('HShape');
                    var hCount = drawHShape();
                    if (hCount) {
                        setPatternAnnouncement(t('text.patternApplied', { shape: 'H', count: hCount }));
                        setTimeout(function () { var _a, _b; return (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.gridParent')) === null || _b === void 0 ? void 0 : _b.focus(); }, 100);
                    }
                    break;
                }
                case 'TShape': {
                    setActionType('');
                    setIsGameMode(true);
                    setCurrentGame('TShape');
                    var tCount = drawTShape();
                    if (tCount) {
                        setPatternAnnouncement(t('text.patternApplied', { shape: 'T', count: tCount }));
                        setTimeout(function () { var _a, _b; return (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.gridParent')) === null || _b === void 0 ? void 0 : _b.focus(); }, 100);
                    }
                    break;
                }
                case 'PlusShape': {
                    setActionType('');
                    setIsGameMode(true);
                    setCurrentGame('PlusShape');
                    var plusCount = drawPlusShape();
                    if (plusCount) {
                        setPatternAnnouncement(t('text.patternApplied', { shape: 'Plus', count: plusCount }));
                        setTimeout(function () { var _a, _b; return (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.gridParent')) === null || _b === void 0 ? void 0 : _b.focus(); }, 100);
                    }
                    break;
                }
                default:
                    break;
            }
        };
        var onEditClick = function (data) {
            if (!isFocusedRef.current)
                return;
            // console.log(data, 'data');
            switch (data.id) {
                case 'stamp_collapsibleBtn':
                    // if (data.data == false) {
                    //   setActionType('');
                    // }
                    break;
                case 'edit':
                    setActiveManipulator('numberCharts');
                    setManiplulatorComponentVisible(true);
                    setSelectedManipulativeId(id);
                    break;
                case 'scaleMinus':
                    var _oldDataMinus_1;
                    var _newDataMinus_1;
                    setManipulative(function (prev) {
                        var _newStateMinus = prev.map(function (item) {
                            var _a, _b;
                            if (item.data.type === 'numberCharts' && item.data.id === id) {
                                _oldDataMinus_1 = (_a = item.data.scale) !== null && _a !== void 0 ? _a : 1;
                                _newDataMinus_1 = Math.max(0.5, ((_b = item.data.scale) !== null && _b !== void 0 ? _b : 1) - 0.1);
                                var _newItem = __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: _newDataMinus_1 }) });
                                return _newItem;
                            }
                            else {
                                return item;
                            }
                        });
                        return _newStateMinus;
                    });
                    if (_oldDataMinus_1 !== undefined && _newDataMinus_1 !== undefined) {
                        saveState({
                            type: id,
                            id: id.split('_')[1],
                            propertyName: 'scale',
                            old: JSON.stringify(_oldDataMinus_1),
                            new: JSON.stringify(_newDataMinus_1),
                        });
                    }
                    if (_newDataMinus_1 !== undefined) {
                        announce(t('text.manipulativeScaled', {
                            name: t('text.manipulativeNumberChart'),
                            direction: t('text.down'),
                            value: _newDataMinus_1.toFixed(1),
                        }));
                    }
                    break;
                case 'scalePlus':
                    var _oldDataPlus_1;
                    var _newDataPlus_1;
                    setManipulative(function (prev) {
                        var _newStatePlus = prev.map(function (item) {
                            var _a, _b;
                            if (item.data.type === 'numberCharts' && item.data.id === id) {
                                _oldDataPlus_1 = (_a = item.data.scale) !== null && _a !== void 0 ? _a : 1;
                                _newDataPlus_1 = ((_b = item.data.scale) !== null && _b !== void 0 ? _b : 1) + 0.1;
                                var newDataPlus = __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: _newDataPlus_1 }) });
                                return newDataPlus;
                            }
                            else {
                                return item;
                            }
                        });
                        return _newStatePlus;
                    });
                    if (_oldDataPlus_1 !== undefined && _newDataPlus_1 !== undefined) {
                        saveState({
                            type: id,
                            id: id.split('_')[1],
                            propertyName: 'scale',
                            old: JSON.stringify(_oldDataPlus_1),
                            new: JSON.stringify(_newDataPlus_1),
                        });
                    }
                    if (_newDataPlus_1 !== undefined) {
                        announce(t('text.manipulativeScaled', {
                            name: t('text.manipulativeNumberChart'),
                            direction: t('text.up'),
                            value: _newDataPlus_1.toFixed(1),
                        }));
                    }
                    break;
            }
        };
        var onChoralClick = function (eventData) {
            var _a, _b, _c, _d, _e;
            if (!isFocusedRef.current)
                return;
            // Use callback to get current manipulative state
            // setManipulative(prev => {
            var prev = manipulative;
            var currData = (_a = prev.find(function (m) { return m.type === 'numberCharts' && m.data.id === id; })) === null || _a === void 0 ? void 0 : _a.data;
            switch (eventData.id) {
                case 'choralPlay':
                    setRevealDirectionIndex(eventData.val.direction);
                    revealDirectionIndexRef.current = eventData.val.direction;
                    // If already paused, resume instead of restarting
                    if (pausedRef.current && intervalRef.current) {
                        resumeRevealing();
                    }
                    else if (animationRef.current && indexRef.current > 0 && !intervalRef.current) {
                        // Animation already in progress via manual "next" clicks - continue from current position
                        continueRevealing((_b = SPEED_TIME[eventData.val.speed]) !== null && _b !== void 0 ? _b : SPEED_TIME[1]);
                    }
                    else {
                        startRevealing((_c = SPEED_TIME[eventData.val.speed]) !== null && _c !== void 0 ? _c : SPEED_TIME[1]);
                    }
                    break;
                case 'choralPause':
                    pauseRevealing();
                    break;
                case 'choralNext':
                    // Stop any running animation to prevent double reveals
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    pausedRef.current = false;
                    if (!animationRef.current) {
                        var currentData = dataRef.current;
                        choralSessionActiveRef.current = true;
                        animationRef.current = currentData.cells.map(function (row) {
                            return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: false })); });
                        });
                        updateManipulative(animationRef.current);
                        indexRef.current = 0;
                    }
                    setRevealDirectionIndex(eventData.val.direction);
                    revealDirectionIndexRef.current = eventData.val.direction;
                    revealNextCell();
                    break;
                case 'choralDirection':
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    pausedRef.current = false;
                    indexRef.current = 0;
                    // Hide all cells
                    var currentDataDirection = dataRef.current;
                    var hiddenCells = currentDataDirection.cells.map(function (row) {
                        return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: false, highlightColor: null })); });
                    });
                    animationRef.current = hiddenCells;
                    updateManipulative(hiddenCells);
                    setRevealDirectionIndex(eventData.val.direction);
                    revealDirectionIndexRef.current = eventData.val.direction;
                    break;
                case 'choralSpeed':
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    pausedRef.current = false;
                    indexRef.current = 0;
                    // Hide all cells
                    var currentDataSpeed = dataRef.current;
                    var speedResetCells = currentDataSpeed.cells.map(function (row) {
                        return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: false, highlightColor: null })); });
                    });
                    animationRef.current = speedResetCells;
                    updateManipulative(speedResetCells);
                    break;
                case 'choralReset':
                    // A close/deselect-driven reset (drawer unmount, context menu close)
                    // must not reveal the rest of the grid while a reveal is mid-progress
                    // — whether the user paused via Play/Pause or only stepped with
                    // "Next" (which never touches `pausedRef`). `animationRef` is only
                    // non-null while a reveal has started and hasn't completed/reset,
                    // so it's the right signal here, not `pausedRef` alone. Only an
                    // explicit user-triggered reset (context menu "Reset") may
                    // force-reveal cells mid-progress.
                    if (animationRef.current && !((_d = eventData.val) === null || _d === void 0 ? void 0 : _d.explicit)) {
                        // If a reveal was actively auto-playing (interval still ticking)
                        // when the popup closed, stop the interval so it doesn't keep
                        // silently revealing cells in the background — but preserve
                        // indexRef/animationRef so pressing Play afterward resumes
                        // (`continueRevealing`) from this position instead of restarting.
                        if (intervalRef.current) {
                            clearInterval(intervalRef.current);
                            intervalRef.current = null;
                        }
                        pausedRef.current = true;
                        break;
                    }
                    // No reveal is mid-progress. An *implicit* close only force-shows
                    // every cell if a choral reveal genuinely ran and hasn't been
                    // resolved yet (`choralSessionActiveRef`) — e.g. it finished
                    // naturally while the popup was open. If choral was never used
                    // this session (or the user last set visibility manually via
                    // show/hide, which clears the flag), closing the popup must not
                    // override that manually-chosen visibility. An *explicit* Reset
                    // always force-shows everything regardless, since it's a
                    // deliberate user action.
                    if (!((_e = eventData.val) === null || _e === void 0 ? void 0 : _e.explicit) && !choralSessionActiveRef.current) {
                        break;
                    }
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    pausedRef.current = false;
                    indexRef.current = 0;
                    // Show all cells
                    var currentDataReset = dataRef.current;
                    var resetCells = currentDataReset.cells.map(function (row) {
                        return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: true, highlightColor: null })); });
                    });
                    animationRef.current = null;
                    choralSessionActiveRef.current = false;
                    updateManipulative(resetCells);
                    break;
                case 'stampEvery':
                    if (currData) {
                        stampAtInterval(eventData.val.interval, eventData.val.startNumerator, eventData.val.startDenominator, currData.numberType);
                    }
                    break;
                default:
                    break;
            }
            //   return prev; // Return unchanged state
            // });
        };
        customEventHandler.addEventListener('numberChartGame', onToolClick);
        customEventHandler.addEventListener('bottomPanel', onEditClick);
        customEventHandler.addEventListener('choralDrawer', onChoralClick);
        return function () {
            customEventHandler.removeEventListener('numberChartGame', onToolClick);
            customEventHandler.removeEventListener('bottomPanel', onEditClick);
            customEventHandler.removeEventListener('choralDrawer', onChoralClick);
        };
    }, [id, manipulative]);
    var getCellData = function (r, c) {
        var _a;
        // Use direction/rows/columns from props directly (not dataRef) so rendering
        // reflects the current prop values immediately, rather than the stale ref.
        var _b = mapRowColByDirection(r, c, direction !== null && direction !== void 0 ? direction : 0, rows, columns), row = _b.row, col = _b.col;
        var cellData = (_a = cells === null || cells === void 0 ? void 0 : cells[row]) === null || _a === void 0 ? void 0 : _a[col];
        return { cellData: cellData, rowIdx: row, colIdx: col };
    };
    /**
     * Calculate uniform font size for ALL cells based on the longest content
     * Cell size: 55x55px (with 2px padding = 51x51px content area)
     * Font range: 20-26px
     * All cells will use the SAME font size for visual consistency
     */
    var MIN_FONT_SIZE = 20;
    var uniformFontSize = useMemo(function () {
        var minSize = MIN_FONT_SIZE;
        var maxSize = 26;
        // Find the maximum content length across all cells
        var maxContentLength = 0;
        cells.forEach(function (row) {
            row.forEach(function (cell) {
                if (cell.value) {
                    // Strip MathJax formatting to get actual content
                    var cleanValue = cell.value
                        .replace(/\\\\/g, '')
                        .replace(/\(|\)/g, '')
                        .replace(/frac/g, '')
                        .replace(/[{}]/g, '');
                    maxContentLength = Math.max(maxContentLength, cleanValue.length);
                }
            });
        });
        // Calculate size based on maximum content length
        // Accounting for 51x51px content area (55px - 4px padding)
        // Short content (1-2 chars): max size (26px)
        // Medium content (3-4 chars): scaled down progressively
        // Long content (5+ chars): min size (16px) for better fit
        var fontSize;
        if (maxContentLength <= 2) {
            fontSize = maxSize;
        }
        else if (maxContentLength <= 4) {
            // More conservative scaling for medium content
            fontSize = maxSize - ((maxContentLength - 2) / 2) * (maxSize - minSize) * 0.6;
        }
        else {
            // Use minimum size for long content to ensure fit
            fontSize = minSize;
        }
        // Clamp between min and max
        return Math.max(minSize, Math.min(maxSize, Math.round(fontSize)));
    }, [cells]);
    /**
     * Calculate dynamic cell size based on content
     * Minimum size: 55px, increases for longer content (e.g., 100000, 200000)
     */
    var dynamicCellSize = useMemo(function () {
        var minCellSize = 55;
        // Find the longest content across all cells
        var maxContentLength = 0;
        var hasFraction = false;
        cells.forEach(function (row) {
            row.forEach(function (cell) {
                if (cell.value) {
                    // Check if it's a fraction or mixed number
                    if (cell.value.includes('frac')) {
                        hasFraction = true;
                        // Check for mixed number first (has both integer and fraction)
                        var mixedMatch = cell.value.match(/^\\?\(?-?(\d+)\\frac\{(\d+)\}\{(\d+)\}/);
                        if (mixedMatch) {
                            // Mixed number: integer + space + fraction
                            var integerLen = mixedMatch[1].length;
                            var numLen = mixedMatch[2].length;
                            var denLen = mixedMatch[3].length;
                            var fractionWidth = Math.max(numLen, denLen);
                            // Total width = integer + small gap + fraction width
                            var totalWidth = integerLen + fractionWidth + 0.5;
                            maxContentLength = Math.max(maxContentLength, totalWidth);
                        }
                        else {
                            // Pure fraction (no integer part)
                            var fracMatch = cell.value.match(/\\frac\{(\d+)\}\{(\d+)\}/);
                            if (fracMatch) {
                                var numLen = fracMatch[1].length;
                                var denLen = fracMatch[2].length;
                                maxContentLength = Math.max(maxContentLength, numLen, denLen);
                            }
                        }
                    }
                    else {
                        // For integers/decimals, strip formatting and count
                        var cleanValue = cell.value
                            .replace(/\\\\/g, '')
                            .replace(/\(|\)/g, '')
                            .replace(/-/g, '');
                        maxContentLength = Math.max(maxContentLength, cleanValue.length);
                    }
                }
            });
        });
        // Calculate required cell size
        // Base: ~10px per character at font size 16-20px, plus padding
        var charWidth = uniformFontSize * 0.5; // Reduced from 0.6 to 0.5 for tighter fit
        var padding = 8; // Reduced from 10 to 8 (4px on each side)
        var requiredWidth = maxContentLength * charWidth + padding;
        // For fractions/mixed numbers, add extra height consideration
        if (hasFraction) {
            requiredWidth = Math.max(requiredWidth, minCellSize);
        }
        // Return max of minimum size and calculated size
        return Math.max(minCellSize, Math.ceil(requiredWidth));
    }, [cells, uniformFontSize]);
    var _1 = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _1.startMultiDrag, moveMultiDrag = _1.moveMultiDrag, endMultiDrag = _1.endMultiDrag;
    var onMouseDown = function (e) {
        var _a, _b;
        // Ignore right-click to prevent cell actions on context menu trigger
        if ('button' in e && e.button === 2)
            return;
        if ('touches' in e) {
            lastTouchTimeRef.current = Date.now();
            lastTouchEventTimeRef.current = Date.now();
        }
        else if (Date.now() - lastTouchTimeRef.current < 500) {
            // Skip synthesized mousedown that iOS fires after touchstart
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        // Always set up drag tracking (for both selection and movement)
        var _c = getClientPoint(e), x = _c.x, y = _c.y;
        var parentRect = (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        if (parentRect) {
            var mouseXInParent = (x - parentRect.left) / zoomLevel;
            var mouseYInParent = (y - parentRect.top) / zoomLevel;
            positionDragRef.current = {
                startPos: { x: x, y: y },
                offset: {
                    x: data.center.x - mouseXInParent,
                    y: data.center.y - mouseYInParent,
                },
                hasMoved: false,
                initialCenter: { x: data.center.x, y: data.center.y },
            };
            setIsDraggingSelf(true);
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('touchmove', onMouseMove, {
                passive: false,
            });
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('touchend', onMouseUp);
            startMultiDrag();
        }
        // Cell actions only work when focused
        if (isFocused) {
            var target = e.target;
            var cell = target.closest('.cell');
            if (!cell)
                return;
            var row = Number(cell.dataset.row);
            var col = Number(cell.dataset.col);
            switch (actionType) {
                case 'stamp':
                    stampAction({
                        row: row,
                        col: col,
                        showStamp: true,
                    });
                    break;
                case 'clear':
                    numbersAction({
                        row: row,
                        col: col,
                        hideType: 'single',
                    });
                    break;
                case 'hideStamp':
                    stampAction({
                        row: row,
                        col: col,
                        hideType: 'single',
                    });
                    break;
                default:
                    break;
            }
        }
    };
    var onMouseMove = function (e) {
        var _a, _b;
        if (!positionDragRef.current)
            return;
        // // Don't allow dragging until focused (first click should only select)
        // if (!isFocusedRef.current) return;
        if ('touches' in e)
            e.preventDefault();
        var _c = getClientPoint(e), x = _c.x, y = _c.y;
        var _d = positionDragRef.current, startPos = _d.startPos, offset = _d.offset, hasMoved = _d.hasMoved;
        if (!hasMoved) {
            var dx = x - startPos.x;
            var dy = y - startPos.y;
            if (Math.sqrt(dx * dx + dy * dy) > 5) {
                positionDragRef.current.hasMoved = true;
            }
            else {
                return;
            }
        }
        // Don't move if position is locked
        if (positionLock)
            return;
        var parentRect = (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        if (!parentRect)
            return;
        var mouseXInParent = (x - parentRect.left) / zoomLevel;
        var mouseYInParent = (y - parentRect.top) / zoomLevel;
        var newX = mouseXInParent + offset.x;
        var newY = mouseYInParent + offset.y;
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === id
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newX, y: newY } }) }) : item;
            });
        });
        var initialCenter = positionDragRef.current.initialCenter;
        moveMultiDrag(newX - initialCenter.x, newY - initialCenter.y);
    };
    var onMouseUp = function (e) {
        // Always clean up event listeners, even if positionDragRef is null
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('touchmove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
        window.removeEventListener('touchend', onMouseUp);
        setIsDraggingSelf(false);
        endMultiDrag();
        if (!positionDragRef.current)
            return;
        if (positionDragRef.current.hasMoved) {
            // Dragged - save state for undo/redo and reorder to bring to front
            var oldCenter = positionDragRef.current.initialCenter;
            var newCenter = dataRef.current.center;
            // Save position change for undo/redo
            var entry = {
                type: id,
                id: id.split('_')[1],
                propertyName: 'center',
                old: JSON.stringify(oldCenter),
                new: JSON.stringify(newCenter),
            };
            if (!registerMultiSelectSaveEntries(id, [entry])) {
                saveState(entry);
            }
            // Reorder to bring to front
            setManipulative(function (prev) {
                var index = prev.findIndex(function (item) { return item.data.id === id; });
                if (index === -1)
                    return prev;
                var updated = __spreadArray([], prev, true);
                var movedItem = updated.splice(index, 1)[0];
                updated.push(movedItem); // move to last
                return updated;
            });
        }
        else {
            // Clicked without dragging - route through `selectTool` so Shift /
            // Ctrl / Cmd toggle into the existing selection. The native mouseup
            // event carries the modifier flags; for touchend (no flags) the helper
            // falls back to single-select.
            setSelectedTool(selectTool(e, id, selectedTool, false));
        }
        positionDragRef.current.hasMoved = false;
        positionDragRef.current = null;
    };
    var stampAction = function (_a) {
        var _b, _c, _d, _e, _f, _g, _h;
        var _j = _a === void 0 ? {} : _a, _k = _j.row, row = _k === void 0 ? 0 : _k, _l = _j.col, col = _l === void 0 ? 0 : _l, _m = _j.hideType, hideType = _m === void 0 ? 'all' : _m, _o = _j.showStamp, showStamp = _o === void 0 ? false : _o;
        var currentData = dataRef.current;
        var colorIndex = getColorInfo(colorRef.current).colorIndex;
        var updatedCells = currentData.cells;
        if (showStamp) {
            updatedCells = currentData.cells.map(function (r, rIdx) {
                return r.map(function (c, cIdx) {
                    if (rIdx === row && cIdx === col) {
                        // Get existing stamps
                        var existingIconIndex = c.iconIndex || [];
                        var existingColorIndex_1 = c.colorIndex || [];
                        // Check if this exact stamp+color combination already exists
                        // by checking if stampIndex and colorIndex match at the same position
                        var stampColorExists = existingIconIndex.some(function (icon, idx) { return icon === stampIndex && existingColorIndex_1[idx] === colorIndex; });
                        return __assign(__assign({}, c), { iconIndex: actionType != 'stamp'
                                ? []
                                : stampColorExists
                                    ? existingIconIndex
                                    : __spreadArray(__spreadArray([], existingIconIndex, true), [stampIndex], false), colorIndex: actionType != 'stamp'
                                ? []
                                : stampColorExists
                                    ? existingColorIndex_1
                                    : __spreadArray(__spreadArray([], existingColorIndex_1, true), [colorIndex], false) });
                    }
                    return c;
                });
            });
        }
        else {
            updatedCells = currentData.cells.map(function (r, rIdx) {
                return r.map(function (c, cIdx) {
                    if (hideType === 'all') {
                        return __assign(__assign({}, c), { iconIndex: [], colorIndex: [] });
                    }
                    if (rIdx === row && cIdx === col) {
                        return __assign(__assign({}, c), { iconIndex: [], colorIndex: [] });
                    }
                    return c;
                });
            });
        }
        updateManipulativeWithUndo(updatedCells);
        // Announce stamp action
        if (showStamp) {
            var cellValue = (_d = (_c = (_b = dataRef.current.cells[row]) === null || _b === void 0 ? void 0 : _b[col]) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : '';
            var color_1 = t(colorTranslationKey(colorIndex));
            var shape = t((_e = STAMP_SHAPE_KEYS[stampIndex]) !== null && _e !== void 0 ? _e : 'text.stamp');
            announce(t('text.stampedWith', { value: cellValue, stamp: "".concat(color_1, " ").concat(shape) }));
        }
        else if (hideType === 'single') {
            var cellValue = (_h = (_g = (_f = dataRef.current.cells[row]) === null || _f === void 0 ? void 0 : _f[col]) === null || _g === void 0 ? void 0 : _g.value) !== null && _h !== void 0 ? _h : '';
            announce(t('text.stampRemoved', { value: cellValue }));
        }
        else {
            announce(t('text.allStampsErased'));
        }
    };
    var numbersAction = function (_a) {
        var _b;
        var _c = _a === void 0 ? {} : _a, _d = _c.row, row = _d === void 0 ? 0 : _d, _e = _c.col, col = _e === void 0 ? 0 : _e, _f = _c.hideType, hideType = _f === void 0 ? 'all' : _f, _g = _c.type, type = _g === void 0 ? 'condition' : _g;
        // Showing/hiding a number (single cell or all) invalidates any
        // in-progress choral reveal — reset so the next Play/Next starts from
        // scratch rather than resuming into a grid whose visibility no longer
        // matches, and tell the drawer/context-menu UI (their play/pause state
        // is local, not derived from these refs) so a stale "playing" state
        // doesn't linger or require an extra click.
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        pausedRef.current = false;
        indexRef.current = 0;
        animationRef.current = null;
        // The user just set visibility explicitly — forget any choral session,
        // so a later close of the choral popup doesn't override this with a
        // forced "reveal all" (see the 'choralReset' guard below).
        choralSessionActiveRef.current = false;
        customEventHandler.dispatchEvent('choralDrawer', { id: 'choralInterrupted' });
        var currentData = dataRef.current;
        var updatedCells = currentData.cells.map(function (r, rIdx) {
            return r.map(function (c, cIdx) {
                if (hideType === 'all' && type === 'all') {
                    return __assign(__assign({}, c), { visible: false });
                }
                if (hideType === 'all' && type === 'condition') {
                    return __assign(__assign({}, c), { visible: isAnyCellHidden(currentData.cells) ? true : false });
                }
                if (rIdx === row && cIdx === col) {
                    return __assign(__assign({}, c), { visible: !c.visible });
                }
                return c;
            });
        });
        updateManipulativeWithUndo(updatedCells);
        // Announce number visibility change
        if (hideType === 'single') {
            var cell = (_b = currentData.cells[row]) === null || _b === void 0 ? void 0 : _b[col];
            if (cell) {
                announce(cell.visible
                    ? t('text.numberHidden', { value: cell.value })
                    : t('text.numberShown', { value: cell.value }));
            }
        }
        else {
            announce(isAnyCellHidden(currentData.cells) ? t('text.allNumbersShown') : t('text.allNumbersHidden'));
        }
    };
    var revealNextCell = function () {
        var _a, _b;
        if (!animationRef.current)
            return;
        // console.trace('here');
        var currentData = dataRef.current;
        var totalCells = currentData.rows * currentData.columns;
        if (indexRef.current >= totalCells) {
            // Animation complete - stop interval and notify
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            // Reset animation state so next play starts fresh
            animationRef.current = null;
            customEventHandler.dispatchEvent('choralDrawer', {
                id: 'choralComplete',
            });
            return;
        }
        // Map index to row/col based on revealDirectionIndex
        // Use ref instead of state to avoid stale closure in setInterval callbacks
        var _c = mapRevealIndexToRowCol(indexRef.current, revealDirectionIndexRef.current, currentData.rows, currentData.columns), visualRow = _c.row, visualCol = _c.col;
        // Transform coordinates based on grid's base direction
        var _d = mapRowCol(visualRow, visualCol), row = _d.row, col = _d.col;
        animationRef.current[row][col] = __assign(__assign({}, animationRef.current[row][col]), { visible: true });
        updateManipulative(animationRef.current);
        // Announce the revealed cell value for screen readers
        var cellValue = (_b = (_a = animationRef.current[row][col]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
        var readable = readableValue(cellValue);
        if (readable)
            announce(readable);
        indexRef.current++;
    };
    var startRevealing = function (intervalInSeconds) {
        // Stop old animation
        if (intervalRef.current)
            clearInterval(intervalRef.current);
        pausedRef.current = false;
        indexRef.current = 0;
        choralSessionActiveRef.current = true;
        // Hide all first (sync) - use latest data
        var currentData = dataRef.current;
        animationRef.current = currentData.cells.map(function (row) {
            return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: false })); });
        });
        updateManipulative(animationRef.current);
        // Reveal first cell immediately
        revealNextCell();
        intervalRef.current = setInterval(function () {
            if (pausedRef.current)
                return;
            revealNextCell();
        }, intervalInSeconds * 1000);
    };
    var pauseRevealing = function () {
        pausedRef.current = true;
    };
    var resumeRevealing = function () {
        pausedRef.current = false;
    };
    var continueRevealing = function (intervalInSeconds) {
        // Continue from current position without resetting state
        // Used when user clicked "next" manually before clicking "play"
        pausedRef.current = false;
        intervalRef.current = setInterval(function () {
            if (pausedRef.current)
                return;
            revealNextCell();
        }, intervalInSeconds * 1000);
    };
    var resetRevealing = function () {
        // 1️⃣ Stop animation
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        // 2️��� Reset refs
        pausedRef.current = false;
        indexRef.current = 0;
        // 3️⃣ Hide all cells - use latest data
        var currentData = dataRef.current;
        var resetCells = currentData.cells.map(function (row) {
            return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: true, highlightColor: null })); });
        });
        animationRef.current = resetCells;
        // 4️⃣ Push to React
        updateManipulative(resetCells);
    };
    var updateManipulative = function (cellData) {
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.type === 'numberCharts' && item.data.id === id
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { cells: cellData }) }) : item;
            });
        });
    };
    var updateManipulativeWithUndo = function (cellData) {
        var oldData = structuredClone(dataRef.current);
        updateManipulative(cellData);
        saveState({
            type: id,
            id: id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(__assign(__assign({}, oldData), { cells: cellData })),
        });
    };
    var mapRowCol = function (r, c) {
        var _a;
        var currentData = dataRef.current;
        return mapRowColByDirection(r, c, (_a = currentData.direction) !== null && _a !== void 0 ? _a : 0, currentData.rows, currentData.columns);
    };
    var toggleGridLines = function () {
        var _oldGridBool;
        var _newGridBool;
        setManipulative(function (prev) {
            return prev.map(function (item) {
                if (item.type === 'numberCharts' && item.data.id === id) {
                    var chartData = item.data;
                    _oldGridBool = chartData.showGridLines;
                    _newGridBool = !chartData.showGridLines;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, chartData), { showGridLines: !chartData.showGridLines }) });
                }
                return item;
            });
        });
        if (_oldGridBool !== undefined && _newGridBool !== undefined) {
            saveState({
                type: id,
                id: id.split('_')[1],
                propertyName: 'showGridLines',
                old: JSON.stringify(_oldGridBool),
                new: JSON.stringify(_newGridBool),
            });
        }
    };
    var drawHShape = function () {
        var currentData = dataRef.current;
        // H shape requires minimum 4 columns and 3 rows
        var hWidth = 4;
        var hHeight = Math.min(5, currentData.rows); // H height, max 5 or available rows
        if (currentData.columns < hWidth || currentData.rows < hHeight) {
            // console.warn('Grid too small for H shape. Need at least 4x3.');
            return 0;
        }
        // Calculate random starting position for the H shape
        var maxStartCol = currentData.columns - hWidth;
        var maxStartRow = currentData.rows - hHeight;
        var startCol = Math.floor(Math.random() * (maxStartCol + 1));
        var startRow = Math.floor(Math.random() * (maxStartRow + 1));
        // Reset all cells entirely before drawing H shape
        var cellData = currentData.cells.map(function (row) {
            return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: false, highlightColor: null, iconIndex: [] })); });
        });
        // Define H shape pattern relative to start position
        // H shape: left vertical line, right vertical line, middle horizontal line
        var hCells = [];
        // Left vertical line of H
        for (var r = 0; r < hHeight; r++) {
            hCells.push({ row: startRow + r, col: startCol });
        }
        // Right vertical line of H
        for (var r = 0; r < hHeight; r++) {
            hCells.push({ row: startRow + r, col: startCol + hWidth - 1 });
        }
        // Middle horizontal line of H
        var middleRow = startRow + Math.floor(hHeight / 2);
        for (var c = 1; c < hWidth - 1; c++) {
            hCells.push({ row: middleRow, col: startCol + c });
        }
        // Apply highlight color to all H shape cells (use mapRowCol to handle direction)
        hCells.forEach(function (_a) {
            var row = _a.row, col = _a.col;
            var mapped = mapRowCol(row, col);
            if (cellData[mapped.row] && cellData[mapped.row][mapped.col]) {
                cellData[mapped.row][mapped.col] = __assign(__assign({}, cellData[mapped.row][mapped.col]), { highlightColor: HIGHLIGHT_COLOR });
            }
        });
        // Pick one random cell from H shape to be visible
        var randomIndex = Math.floor(Math.random() * hCells.length);
        var randomCell = hCells[randomIndex];
        var mappedRandom = mapRowCol(randomCell.row, randomCell.col);
        if (cellData[mappedRandom.row] && cellData[mappedRandom.row][mappedRandom.col]) {
            cellData[mappedRandom.row][mappedRandom.col] = __assign(__assign({}, cellData[mappedRandom.row][mappedRandom.col]), { visible: true });
        }
        updateManipulativeWithUndo(cellData);
        return hCells.length;
    };
    var drawTShape = function () {
        var currentData = dataRef.current;
        // T shape requires 5 columns and 5 rows
        var tWidth = 5;
        var tHeight = 5;
        if (currentData.columns < tWidth || currentData.rows < tHeight) {
            // console.warn('Grid too small for T shape. Need at least 5x5.');
            return 0;
        }
        // Calculate random starting position for the T shape
        var maxStartCol = currentData.columns - tWidth;
        var maxStartRow = currentData.rows - tHeight;
        var startCol = Math.floor(Math.random() * (maxStartCol + 1));
        var startRow = Math.floor(Math.random() * (maxStartRow + 1));
        // Reset all cells entirely before drawing T shape
        var cellData = currentData.cells.map(function (row) {
            return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: false, highlightColor: null, iconIndex: [] })); });
        });
        // Define T shape pattern relative to start position
        // T shape: top horizontal line (full width) + vertical line down the middle
        var tCells = [];
        // Top horizontal line of T (5 cells)
        for (var c = 0; c < tWidth; c++) {
            tCells.push({ row: startRow, col: startCol + c });
        }
        // Vertical line of T (middle column, starting from row 1)
        var middleCol = startCol + Math.floor(tWidth / 2);
        for (var r = 1; r < tHeight; r++) {
            tCells.push({ row: startRow + r, col: middleCol });
        }
        // Apply highlight color to all T shape cells (use mapRowCol to handle direction)
        tCells.forEach(function (_a) {
            var row = _a.row, col = _a.col;
            var mapped = mapRowCol(row, col);
            if (cellData[mapped.row] && cellData[mapped.row][mapped.col]) {
                cellData[mapped.row][mapped.col] = __assign(__assign({}, cellData[mapped.row][mapped.col]), { highlightColor: HIGHLIGHT_COLOR });
            }
        });
        // Pick one random cell from T shape to be visible
        var randomIndex = Math.floor(Math.random() * tCells.length);
        var randomCell = tCells[randomIndex];
        var mappedRandom = mapRowCol(randomCell.row, randomCell.col);
        if (cellData[mappedRandom.row] && cellData[mappedRandom.row][mappedRandom.col]) {
            cellData[mappedRandom.row][mappedRandom.col] = __assign(__assign({}, cellData[mappedRandom.row][mappedRandom.col]), { visible: true });
        }
        updateManipulativeWithUndo(cellData);
        return tCells.length;
    };
    var drawPlusShape = function () {
        var currentData = dataRef.current;
        // Randomly choose between small (3x3) and large (5x5) plus shape
        var canUseLarge = currentData.columns >= 5 && currentData.rows >= 5;
        var useLarge = canUseLarge && Math.random() < 0.5;
        var plusWidth = useLarge ? 5 : 3;
        var plusHeight = useLarge ? 5 : 3;
        if (currentData.columns < 3 || currentData.rows < 3) {
            // console.warn('Grid too small for Plus shape. Need at least 3x3.');
            return 0;
        }
        // Calculate random starting position for the Plus shape
        var maxStartCol = currentData.columns - plusWidth;
        var maxStartRow = currentData.rows - plusHeight;
        var startCol = Math.floor(Math.random() * (maxStartCol + 1));
        var startRow = Math.floor(Math.random() * (maxStartRow + 1));
        // Reset all cells entirely before drawing Plus shape
        var cellData = currentData.cells.map(function (row) {
            return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: false, highlightColor: null, iconIndex: [] })); });
        });
        var plusCells = [];
        var middleCol = startCol + Math.floor(plusWidth / 2);
        var middleRow = startRow + Math.floor(plusHeight / 2);
        if (useLarge) {
            // Large 5x5 Plus shape:
            // Row 0: middle cell
            // Row 1: middle cell
            // Row 2: all 5 cells (horizontal line)
            // Row 3: middle cell
            // Row 4: middle cell
            // Top vertical (rows 0 and 1, middle column)
            plusCells.push({ row: startRow, col: middleCol });
            plusCells.push({ row: startRow + 1, col: middleCol });
            // Horizontal line (row 2, all 5 columns)
            for (var c = 0; c < plusWidth; c++) {
                plusCells.push({ row: middleRow, col: startCol + c });
            }
            // Bottom vertical (rows 3 and 4, middle column)
            plusCells.push({ row: startRow + 3, col: middleCol });
            plusCells.push({ row: startRow + 4, col: middleCol });
        }
        else {
            // Small 3x3 Plus shape:
            // Row 0: middle cell
            // Row 1: all 3 cells (horizontal line)
            // Row 2: middle cell
            // Top vertical (row 0, middle column)
            plusCells.push({ row: startRow, col: middleCol });
            // Horizontal line (row 1, all 3 columns)
            for (var c = 0; c < plusWidth; c++) {
                plusCells.push({ row: middleRow, col: startCol + c });
            }
            // Bottom vertical (row 2, middle column)
            plusCells.push({ row: startRow + 2, col: middleCol });
        }
        // Apply highlight color to all Plus shape cells (use mapRowCol to handle direction)
        plusCells.forEach(function (_a) {
            var row = _a.row, col = _a.col;
            var mapped = mapRowCol(row, col);
            if (cellData[mapped.row] && cellData[mapped.row][mapped.col]) {
                cellData[mapped.row][mapped.col] = __assign(__assign({}, cellData[mapped.row][mapped.col]), { highlightColor: HIGHLIGHT_COLOR });
            }
        });
        // Pick one random cell from Plus shape to be visible
        var randomIndex = Math.floor(Math.random() * plusCells.length);
        var randomCell = plusCells[randomIndex];
        var mappedRandom = mapRowCol(randomCell.row, randomCell.col);
        if (cellData[mappedRandom.row] && cellData[mappedRandom.row][mappedRandom.col]) {
            cellData[mappedRandom.row][mappedRandom.col] = __assign(__assign({}, cellData[mappedRandom.row][mappedRandom.col]), { visible: true });
        }
        updateManipulativeWithUndo(cellData);
        return plusCells.length;
    };
    var drawSquareShape = function () {
        var currentData = dataRef.current;
        // Collect all valid square sizes (2x2, 3x3, 4x4, 5x5) that fit the grid
        var possibleSizes = [2, 3, 4, 5].filter(function (size) { return currentData.columns >= size && currentData.rows >= size; });
        if (possibleSizes.length === 0) {
            return 0;
        }
        var squareSize = possibleSizes[Math.floor(Math.random() * possibleSizes.length)];
        // Calculate random starting position for the Square shape
        var maxStartCol = currentData.columns - squareSize;
        var maxStartRow = currentData.rows - squareSize;
        var startCol = Math.floor(Math.random() * (maxStartCol + 1));
        var startRow = Math.floor(Math.random() * (maxStartRow + 1));
        // Reset all cells entirely before drawing Square shape
        var cellData = currentData.cells.map(function (row) {
            return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: false, highlightColor: null, iconIndex: [] })); });
        });
        // Define Square shape pattern (fully filled)
        var squareCells = [];
        // Fill all cells in the square
        for (var r = 0; r < squareSize; r++) {
            for (var c = 0; c < squareSize; c++) {
                squareCells.push({ row: startRow + r, col: startCol + c });
            }
        }
        // Apply highlight color to all Square shape cells (use mapRowCol to handle direction)
        squareCells.forEach(function (_a) {
            var row = _a.row, col = _a.col;
            var mapped = mapRowCol(row, col);
            if (cellData[mapped.row] && cellData[mapped.row][mapped.col]) {
                cellData[mapped.row][mapped.col] = __assign(__assign({}, cellData[mapped.row][mapped.col]), { highlightColor: HIGHLIGHT_COLOR });
            }
        });
        // Pick one random cell from Square shape to be visible
        var randomIndex = Math.floor(Math.random() * squareCells.length);
        var randomCell = squareCells[randomIndex];
        var mappedRandom = mapRowCol(randomCell.row, randomCell.col);
        if (cellData[mappedRandom.row] && cellData[mappedRandom.row][mappedRandom.col]) {
            cellData[mappedRandom.row][mappedRandom.col] = __assign(__assign({}, cellData[mappedRandom.row][mappedRandom.col]), { visible: true });
        }
        updateManipulativeWithUndo(cellData);
        return squareCells.length;
    };
    var drawCircleShape = function () {
        var currentData = dataRef.current;
        // prettier-ignore
        var circlePatterns = {
            4: [
                [0, 1], [0, 2],
                [1, 0], [1, 3],
                [2, 0], [2, 3],
                [3, 1], [3, 2],
            ],
            5: [
                [0, 1], [0, 2], [0, 3],
                [1, 0], [1, 4],
                [2, 0], [2, 4],
                [3, 0], [3, 4],
                [4, 1], [4, 2], [4, 3],
            ],
            7: [
                [0, 2], [0, 3], [0, 4],
                [1, 1], [1, 5],
                [2, 0], [2, 6],
                [3, 0], [3, 6],
                [4, 0], [4, 6],
                [5, 1], [5, 5],
                [6, 2], [6, 3], [6, 4],
            ],
            8: [
                [0, 2], [0, 3], [0, 4], [0, 5],
                [1, 1], [1, 6],
                [2, 0], [2, 7],
                [3, 0], [3, 7],
                [4, 0], [4, 7],
                [5, 0], [5, 7],
                [6, 1], [6, 6],
                [7, 2], [7, 3], [7, 4], [7, 5],
            ],
            9: [
                [0, 3], [0, 4], [0, 5],
                [1, 2], [1, 6],
                [2, 1], [2, 7],
                [3, 0], [3, 8],
                [4, 0], [4, 8],
                [5, 0], [5, 8],
                [6, 1], [6, 7],
                [7, 2], [7, 6],
                [8, 3], [8, 4], [8, 5],
            ],
            10: [
                [0, 4], [0, 5],
                [1, 2], [1, 3], [1, 6], [1, 7],
                [2, 1], [2, 8],
                [3, 1], [3, 8],
                [4, 0], [4, 9],
                [5, 0], [5, 9],
                [6, 1], [6, 8],
                [7, 1], [7, 8],
                [8, 2], [8, 3], [8, 6], [8, 7],
                [9, 4], [9, 5],
            ],
        };
        // Collect all valid circle sizes that fit the grid
        var possibleSizes = Object.keys(circlePatterns)
            .map(Number)
            .filter(function (size) { return currentData.columns >= size && currentData.rows >= size; });
        if (possibleSizes.length === 0) {
            return 0;
        }
        var circleSize = possibleSizes[Math.floor(Math.random() * possibleSizes.length)];
        // Calculate random starting position for the Circle shape
        var maxStartCol = currentData.columns - circleSize;
        var maxStartRow = currentData.rows - circleSize;
        var startCol = Math.floor(Math.random() * (maxStartCol + 1));
        var startRow = Math.floor(Math.random() * (maxStartRow + 1));
        // Reset all cells entirely before drawing Circle shape
        var cellData = currentData.cells.map(function (row) {
            return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: false, highlightColor: null, iconIndex: [] })); });
        });
        // Build circle cells from pattern offsets
        var circleCells = circlePatterns[circleSize].map(function (_a) {
            var r = _a[0], c = _a[1];
            return ({
                row: startRow + r,
                col: startCol + c,
            });
        });
        // Apply highlight color to all Circle shape cells (use mapRowCol to handle direction)
        circleCells.forEach(function (_a) {
            var row = _a.row, col = _a.col;
            var mapped = mapRowCol(row, col);
            if (cellData[mapped.row] && cellData[mapped.row][mapped.col]) {
                cellData[mapped.row][mapped.col] = __assign(__assign({}, cellData[mapped.row][mapped.col]), { highlightColor: HIGHLIGHT_COLOR });
            }
        });
        // Pick one random cell from Circle shape to be visible
        var randomIndex = Math.floor(Math.random() * circleCells.length);
        var randomCell = circleCells[randomIndex];
        var mappedRandom = mapRowCol(randomCell.row, randomCell.col);
        if (cellData[mappedRandom.row] && cellData[mappedRandom.row][mappedRandom.col]) {
            cellData[mappedRandom.row][mappedRandom.col] = __assign(__assign({}, cellData[mappedRandom.row][mappedRandom.col]), { visible: true });
        }
        updateManipulativeWithUndo(cellData);
        return circleCells.length;
    };
    var drawLineShape = function () {
        var currentData = dataRef.current;
        var lineLength = 5;
        // Check if grid is large enough for a line of length 5
        var canDrawHorizontal = currentData.columns >= lineLength;
        var canDrawVertical = currentData.rows >= lineLength;
        if (!canDrawHorizontal && !canDrawVertical) {
            // console.warn('Grid too small for Line shape. Need at least 5 columns or 5 rows.');
            return 0;
        }
        // Randomly choose orientation based on what's possible
        var isHorizontal;
        if (canDrawHorizontal && canDrawVertical) {
            isHorizontal = Math.random() < 0.5;
        }
        else {
            isHorizontal = canDrawHorizontal;
        }
        // Calculate random starting position for the line
        var startCol;
        var startRow;
        if (isHorizontal) {
            var maxStartCol = currentData.columns - lineLength;
            startCol = Math.floor(Math.random() * (maxStartCol + 1));
            startRow = Math.floor(Math.random() * currentData.rows);
        }
        else {
            startCol = Math.floor(Math.random() * currentData.columns);
            var maxStartRow = currentData.rows - lineLength;
            startRow = Math.floor(Math.random() * (maxStartRow + 1));
        }
        // Keep all cells visible, just reset highlight and iconIndex
        var cellData = currentData.cells.map(function (row) {
            return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: true, highlightColor: null, iconIndex: [] })); });
        });
        // Define line cells
        var lineCells = [];
        if (isHorizontal) {
            for (var c = 0; c < lineLength; c++) {
                lineCells.push({ row: startRow, col: startCol + c });
            }
        }
        else {
            for (var r = 0; r < lineLength; r++) {
                lineCells.push({ row: startRow + r, col: startCol });
            }
        }
        // Pick one random cell from line to be visible
        var randomIndex = Math.floor(Math.random() * lineCells.length);
        // Apply highlight color to all line cells and set visibility
        // Only the random cell is visible, rest are hidden
        lineCells.forEach(function (_a, index) {
            var row = _a.row, col = _a.col;
            var mapped = mapRowCol(row, col);
            if (cellData[mapped.row] && cellData[mapped.row][mapped.col]) {
                cellData[mapped.row][mapped.col] = __assign(__assign({}, cellData[mapped.row][mapped.col]), { highlightColor: HIGHLIGHT_COLOR, visible: index === randomIndex });
            }
        });
        updateManipulativeWithUndo(cellData);
        return lineCells.length;
    };
    var drawOneLessOneMore = function () {
        var currentData = dataRef.current;
        var cellCount = 3; // Always 3 cells: one less, center, one more
        if (currentData.columns < cellCount) {
            // console.warn('Grid too small for One Less One More. Need at least 3 columns.');
            return;
        }
        // Calculate random starting position (need 3 consecutive horizontal cells)
        var maxStartCol = currentData.columns - cellCount;
        var startCol = Math.floor(Math.random() * (maxStartCol + 1));
        var startRow = Math.floor(Math.random() * currentData.rows);
        // Keep all cells visible, reset highlight and iconIndex
        var cellData = currentData.cells.map(function (row) {
            return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: true, highlightColor: null, iconIndex: [] })); });
        });
        // Define the 3 horizontal cells: one less, center, one more
        var threeCells = [
            { row: startRow, col: startCol }, // One less
            { row: startRow, col: startCol + 1 }, // Center
            { row: startRow, col: startCol + 2 }, // One more
        ];
        // Apply highlight and visibility to the 3 cells
        // Center cell (index 1) is visible with purple color, the other 2 are hidden with yellow
        threeCells.forEach(function (_a, index) {
            var row = _a.row, col = _a.col;
            var mapped = mapRowCol(row, col);
            if (cellData[mapped.row] && cellData[mapped.row][mapped.col]) {
                cellData[mapped.row][mapped.col] = __assign(__assign({}, cellData[mapped.row][mapped.col]), { highlightColor: index === 1 ? '#c199f0' : HIGHLIGHT_COLOR, visible: index === 1 });
            }
        });
        updateManipulativeWithUndo(cellData);
    };
    var stampAtInterval = function (interval, startNumerator, startDenominator, numberType) {
        if (startDenominator === void 0) { startDenominator = 1; }
        if (numberType === void 0) { numberType = 0; }
        var currentData = dataRef.current;
        // Flatten cells to a 1D array with their positions
        var flatCells = [];
        currentData.cells.forEach(function (row, rowIdx) {
            row.forEach(function (cell, colIdx) {
                flatCells.push({ cell: cell, rowIdx: rowIdx, colIdx: colIdx });
            });
        });
        // Find the index of the start cell
        var startIndex = flatCells.findIndex(function (_a) {
            var cell = _a.cell;
            return compareCellValues(cell, startNumerator, startDenominator, numberType);
        });
        if (startIndex === -1) {
            // console.warn(`Start number ${startNumerator}/${startDenominator} not found in cells`);
            return;
        }
        var cellData = structuredClone(currentData.cells);
        // Stamp cells at interval positions starting from startIndex
        var clrIndex = getColorInfo(colorRef.current).colorIndex;
        var currentStampIndex = stampIndexRef.current;
        for (var i = startIndex; i < flatCells.length; i += interval) {
            var _a = flatCells[i], rowIdx = _a.rowIdx, colIdx = _a.colIdx;
            var currentCell = cellData[rowIdx][colIdx];
            // Append new stamp to existing stamps (avoid duplicates)
            var existingIconIndex = currentCell.iconIndex || [];
            var existingColorIndex = currentCell.colorIndex || [];
            // Check if this stamp already exists at this position
            var stampExists = existingIconIndex.includes(currentStampIndex);
            cellData[rowIdx][colIdx] = __assign(__assign({}, currentCell), { iconIndex: stampExists ? existingIconIndex : __spreadArray(__spreadArray([], existingIconIndex, true), [currentStampIndex], false), colorIndex: stampExists ? existingColorIndex : __spreadArray(__spreadArray([], existingColorIndex, true), [clrIndex], false) });
        }
        updateManipulativeWithUndo(cellData);
    };
    var missingNumberShape = function () {
        var currentData = dataRef.current;
        var randomRow = Math.floor(Math.random() * currentData.rows);
        var randomCol = Math.floor(Math.random() * currentData.columns);
        var cellData = currentData.cells.map(function (row, rIdx) {
            return row.map(function (cell, cIdx) {
                var mapped = mapRowCol(rIdx, cIdx);
                var updatedCell = __assign(__assign({}, cell), { visible: true, highlightColor: null, iconIndex: [] });
                if (mapped.row === randomRow && mapped.col === randomCol) {
                    return __assign(__assign({}, updatedCell), { visible: false, highlightColor: HIGHLIGHT_COLOR });
                }
                return updatedCell;
            });
        });
        updateManipulativeWithUndo(cellData);
    };
    /** ---------------- BOTTOM PANEL EVENT ---------------- */
    useEffect(function () {
        var onToolClick = function (eventData) {
            var _a;
            if (!isFocusedRef.current)
                return;
            switch (eventData.id) {
                case 'edit':
                    setActiveManipulator('numberCharts');
                    setManiplulatorComponentVisible(true);
                    setSelectedManipulativeId(id);
                    break;
                case 'gride':
                    setActionType('');
                    toggleGridLines();
                    announce(t(dataRef.current.showGridLines ? 'text.gridLinesHidden' : 'text.gridLinesShown'));
                    break;
                case 'showHideNumbers':
                    setActionType(function (prev) {
                        if (prev === 'clear')
                            return '';
                        announce(t('text.showHideNumberModeAnnounce'));
                        return 'clear';
                    });
                    break;
                case 'showAllHideNumbers':
                    setActionType('');
                    numbersAction({
                        hideType: 'all',
                        type: 'condition',
                    });
                    announce(t('text.allNumbersToggled'));
                    break;
                case 'game':
                    setActionType('');
                    if (dataRef.current.rows < 5 || dataRef.current.columns < 5) {
                        setActiveManipulator('chartSizeAlert');
                    }
                    else {
                        setActiveManipulator('numberChartGame');
                    }
                    setManiplulatorComponentVisible(true);
                    announce(t('text.gameStarted'));
                    break;
                case 'replay':
                    if (isGameMode && currentGame) {
                        switch (currentGame) {
                            case 'MissingNumber':
                                missingNumberShape();
                                break;
                            case 'OneMoreOneLess':
                                drawOneLessOneMore();
                                break;
                            case 'Line':
                                drawLineShape();
                                break;
                            case 'Square':
                                drawSquareShape();
                                break;
                            case 'Circle':
                                drawCircleShape();
                                break;
                            case 'HShape':
                                drawHShape();
                                break;
                            case 'TShape':
                                drawTShape();
                                break;
                            case 'PlusShape':
                                drawPlusShape();
                                break;
                        }
                        announce(t('text.gameReplayed'));
                    }
                    break;
                case 'exitGames':
                    setIsGameMode(false);
                    setCurrentGame(null);
                    setPatternAnnouncement(null);
                    // Reset all cells: make visible, remove highlights and stamps
                    var currentDataExit = dataRef.current;
                    var resetCellData = currentDataExit.cells.map(function (row) {
                        return row.map(function (cell) { return (__assign(__assign({}, cell), { visible: true, highlightColor: null, iconIndex: [], colorIndex: [] })); });
                    });
                    updateManipulativeWithUndo(resetCellData);
                    announce(t('text.gameExited'));
                    break;
                case 'stamp':
                    // Use refs to avoid stale-closure bug: this useEffect only re-subscribes
                    // on stampIndex changes, not on actionType, so closure-captured
                    // `actionType` goes stale after the user toggles stamp off.
                    if (actionTypeRef.current === 'stamp' && stampIndexRef.current === eventData.data) {
                        setActionType('');
                    }
                    else {
                        setActionType('stamp');
                        setStampIndex(eventData.data);
                        var shapeName = t((_a = STAMP_SHAPE_KEYS[eventData.data]) !== null && _a !== void 0 ? _a : 'text.stamp');
                        announce(t('text.stampModeAnnounce', { shape: shapeName }));
                    }
                    break;
                case 'stampEraser':
                    setActionType('hideStamp');
                    announce(t('text.eraseStampModeAnnounce'));
                    break;
                case 'stampEraserAll':
                    stampAction({
                        hideType: 'all',
                    });
                    setActionType('');
                    break;
            }
        };
        customEventHandler.addEventListener('bottomPanel', onToolClick);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
        };
    }, [id, isGameMode, currentGame, stampIndex]);
    // Listen for context menu's grid-mode entry request
    useEffect(function () {
        if (!isFocused)
            return;
        var onEnterGridMode = function () {
            setGridNavActive(true);
            setActiveCell({ row: 0, col: 0 });
            requestAnimationFrame(function () {
                var _a;
                (_a = cellRefs.current.get('0_0')) === null || _a === void 0 ? void 0 : _a.focus();
            });
        };
        customEventHandler.addEventListener('ncEnterGridMode', onEnterGridMode);
        return function () {
            customEventHandler.removeEventListener('ncEnterGridMode', onEnterGridMode);
        };
    }, [isFocused]);
    // Removed posData state and DOM measurement useEffect - no longer needed with CellValue component
    if (isFocused) {
        // console.log(data, 'data');
    }
    var handleCellKeyDown = function (e, r, c) {
        var _a, _b;
        var totalRows = data.rows;
        var totalCols = data.columns;
        var nextRow = r;
        var nextCol = c;
        switch (e.key) {
            case 'ArrowRight':
                e.preventDefault();
                e.stopPropagation();
                nextCol = c + 1;
                if (nextCol >= totalCols) {
                    nextCol = 0;
                    nextRow = (r + 1) % totalRows;
                }
                break;
            case 'ArrowLeft':
                e.preventDefault();
                e.stopPropagation();
                nextCol = c - 1;
                if (nextCol < 0) {
                    nextCol = totalCols - 1;
                    nextRow = (r - 1 + totalRows) % totalRows;
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                e.stopPropagation();
                nextRow = (r + 1) % totalRows;
                break;
            case 'ArrowUp':
                e.preventDefault();
                e.stopPropagation();
                nextRow = (r - 1 + totalRows) % totalRows;
                break;
            case 'Home':
                e.preventDefault();
                nextCol = 0;
                if (e.ctrlKey)
                    nextRow = 0;
                break;
            case 'End':
                e.preventDefault();
                nextCol = totalCols - 1;
                if (e.ctrlKey)
                    nextRow = totalRows - 1;
                break;
            case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                setGridNavActive(false);
                // Focus back to gridParent button
                (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.gridParent')) === null || _b === void 0 ? void 0 : _b.focus();
                return;
            case 'Tab':
                setGridNavActive(false);
                return; // Let Tab propagate naturally
            case 'Enter':
            case ' ':
                e.preventDefault();
                e.stopPropagation();
                // Perform cell action based on current mode
                if (actionType === 'stamp') {
                    onMouseDown({
                        target: cellRefs.current.get("".concat(r, "_").concat(c)),
                        preventDefault: function () { },
                        stopPropagation: function () { },
                    });
                }
                else if (actionType === 'clear') {
                    onMouseDown({
                        target: cellRefs.current.get("".concat(r, "_").concat(c)),
                        preventDefault: function () { },
                        stopPropagation: function () { },
                    });
                }
                else if (actionType === 'hideStamp') {
                    onMouseDown({
                        target: cellRefs.current.get("".concat(r, "_").concat(c)),
                        preventDefault: function () { },
                        stopPropagation: function () { },
                    });
                }
                return;
            default:
                return;
        }
        setActiveCell({ row: nextRow, col: nextCol });
        requestAnimationFrame(function () {
            var _a;
            (_a = cellRefs.current.get("".concat(nextRow, "_").concat(nextCol))) === null || _a === void 0 ? void 0 : _a.focus();
        });
    };
    return (_jsx("div", { className: "numberChartTable numberCharts".concat(id), ref: wrapperRef, "data-manipulative-id": id, role: gridNavActive ? 'application' : undefined, style: {
            position: calledFrom === 'popup' ? 'unset' : 'absolute',
            left: ((_f = data.center) === null || _f === void 0 ? void 0 : _f.x) || 100,
            top: ((_g = data.center) === null || _g === void 0 ? void 0 : _g.y) || 100,
            // border: `2px solid ${isFocused && calledFrom === 'widget' ? 'black' : 'transparent'}`,
            boxShadow: isFocused && calledFrom === 'widget' ? '0 0 20px 0 #5063AE' : 'none',
            zIndex: isDraggingSelf ? 999 : undefined,
            transform: "translate(-50%, -50%) scale(".concat(calledFrom === 'popup' ? 1 : (data === null || data === void 0 ? void 0 : data.scale) || 1, ")"),
            transformOrigin: 'center center',
            '--maxDataWidth': "".concat(dynamicCellSize, "px"),
        }, id: "numberChartTable".concat(id).concat(calledFrom), children: _jsx("button", { role: gridNavActive ? 'grid' : 'img', className: "gridParent", tabIndex: calledFrom === 'widget' ? 0 : -1, "aria-label": (_h = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _h !== void 0 ? _h : (patternAnnouncement
                ? "".concat(patternAnnouncement, ". ").concat(t('text.numberChartAriaGridDesc', { rows: rows, columns: columns }), ". ").concat(t('text.numberChartTableDimensions', { rows: rows, columns: columns }))
                : "".concat(t('text.numberChartAriaGridDesc', { rows: rows, columns: columns }), ". ").concat(t('text.numberChartTableDimensions', { rows: rows, columns: columns }))), style: {
                gridTemplateColumns: "repeat(".concat(columns, ", 1fr)"),
                gridTemplateRows: "repeat(".concat(rows, ", 1fr)"),
                border: "10px solid ".concat(data.showGridLines ? '#4586C0' : 'transparent'),
                margin: '0px',
                padding: '0px',
                background: 'transparent',
                // width: data.dimensions?.width,
                // height: data.dimensions?.height,
                cursor: calledFrom === 'popup'
                    ? 'inherit'
                    : panningEnable
                        ? 'inherit'
                        : positionLock
                            ? 'default'
                            : positionDragRef.current
                                ? 'grabbing'
                                : 'grab',
            }, onMouseDown: onMouseDown, onTouchStart: onMouseDown, onTouchEnd: function () {
                lastTouchEventTimeRef.current = Date.now();
            }, onFocus: function (e) {
                var _a, _b;
                try {
                    lastFocusWasScreenReader.current = !!((_b = (_a = e.target).matches) === null || _b === void 0 ? void 0 : _b.call(_a, ':focus-visible'));
                }
                catch (_c) {
                    lastFocusWasScreenReader.current = false;
                }
            }, onBlur: function () {
                lastFocusWasScreenReader.current = false;
            }, "aria-haspopup": "menu", onClick: function (e) {
                var _a;
                if (calledFrom !== 'widget')
                    return;
                // Multi-layer VoiceOver double-tap detection (iPad):
                //   Layer 1 — synthetic click with detail === 0 (most iPadOS versions)
                //   Layer 2 — :focus-visible was set by VO on focus (iOS 15+)
                //   Layer 3 — click with no recent touchstart/end → synthetic
                var recentTouch = Date.now() - lastTouchEventTimeRef.current < 150;
                var isVOClick = IS_TOUCH_DEVICE &&
                    (e.detail === 0 || lastFocusWasScreenReader.current || !recentTouch);
                if (isVOClick) {
                    lastFocusWasScreenReader.current = false;
                    if (!selectedTool.includes(id))
                        setSelectedTool([id]);
                    var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                    if (rect) {
                        setContextMenu({ x: rect.right + 8, y: rect.top, manipulativeId: id });
                    }
                }
            }, onKeyDown: function (e) {
                var _a, _b, _c, _d;
                if (calledFrom === 'popup' || panningEnable)
                    return;
                // F2: Enter grid cell navigation mode
                if (e.key === 'F2') {
                    e.preventDefault();
                    e.stopPropagation();
                    setGridNavActive(true);
                    setActiveCell({ row: 0, col: 0 });
                    requestAnimationFrame(function () {
                        var _a;
                        (_a = cellRefs.current.get('0_0')) === null || _a === void 0 ? void 0 : _a.focus();
                    });
                    return;
                }
                // Context menu: Enter, Space, Shift+F10, ContextMenu key
                if (e.key === 'Enter' ||
                    e.key === ' ' ||
                    e.key === 'ContextMenu' ||
                    (e.shiftKey && e.key === 'F10')) {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!selectedTool.includes(id)) {
                        setSelectedTool([id]);
                    }
                    var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                    if (rect) {
                        setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                    }
                    return;
                }
                // Arrow keys: move chart (blocked when position locked)
                var posLock = (_c = (_b = data.lock) === null || _b === void 0 ? void 0 : _b.positioning) !== null && _c !== void 0 ? _c : false;
                if (posLock)
                    return;
                var STEP = e.shiftKey ? 50 : 10;
                var delta = {
                    ArrowUp: { x: 0, y: -STEP },
                    ArrowDown: { x: 0, y: STEP },
                    ArrowLeft: { x: -STEP, y: 0 },
                    ArrowRight: { x: STEP, y: 0 },
                };
                if (delta[e.key]) {
                    e.preventDefault();
                    e.stopPropagation();
                    var d = delta[e.key];
                    var oldCenter = (_d = data.center) !== null && _d !== void 0 ? _d : { x: 0, y: 0 };
                    var newCenter_1 = { x: oldCenter.x + d.x, y: oldCenter.y + d.y };
                    setManipulative(function (prev) {
                        return prev.map(function (m) {
                            return m.data.id === id ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: newCenter_1 }) }) : m;
                        });
                    });
                }
            }, onKeyUp: function (e) {
                var _a, _b, _c, _d, _e, _f;
                // Save state on arrow key release (same pattern as Card)
                if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) &&
                    calledFrom === 'widget') {
                    var posLock = (_b = (_a = data.lock) === null || _a === void 0 ? void 0 : _a.positioning) !== null && _b !== void 0 ? _b : false;
                    if (posLock)
                        return;
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(data.center),
                        new: JSON.stringify(data.center),
                    });
                    // Announce movement direction and position
                    var dirMap = {
                        ArrowUp: t('text.up'),
                        ArrowDown: t('text.down'),
                        ArrowLeft: t('text.left'),
                        ArrowRight: t('text.right'),
                    };
                    announce(t('text.manipulativeMoved', {
                        name: t('text.manipulativeNumberChart'),
                        direction: dirMap[e.key] || '',
                        x: Math.round((_d = (_c = data.center) === null || _c === void 0 ? void 0 : _c.x) !== null && _d !== void 0 ? _d : 0),
                        y: Math.round((_f = (_e = data.center) === null || _e === void 0 ? void 0 : _e.y) !== null && _f !== void 0 ? _f : 0),
                    }));
                }
            }, children: Array.from({ length: data.rows }).map(function (_, r) { return (_jsx("div", { role: "row", "aria-rowindex": r + 1, style: { display: 'contents' }, children: Array.from({ length: data.columns }).map(function (_, c) {
                    var _a, _b;
                    var _c = getCellData(r, c), cellData = _c.cellData, rowIdx = _c.rowIdx, colIdx = _c.colIdx;
                    var stamps = getStampElements(cellData);
                    return (_jsx(NumberChartCell, { cellData: cellData, rowIdx: rowIdx, colIdx: colIdx, r: r, c: c, uniformFontSize: uniformFontSize, showGridLines: data.showGridLines, stamps: stamps, cellBorderWidth: cellBorderWidth, cellTabIndex: gridNavActive
                            ? activeCell.row === r && activeCell.col === c
                                ? 0
                                : -1
                            : undefined, cellRef: gridNavActive
                            ? function (el) {
                                if (el)
                                    cellRefs.current.set("".concat(r, "_").concat(c), el);
                                else
                                    cellRefs.current.delete("".concat(r, "_").concat(c));
                            }
                            : undefined, onCellKeyDown: gridNavActive ? handleCellKeyDown : undefined, isStamped: ((_b = (_a = cellData === null || cellData === void 0 ? void 0 : cellData.iconIndex) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 }, "".concat(r, "-").concat(c)));
                }) }, "row-".concat(r))); }) }) }));
};
export default NumberCharts;
